import { useState } from 'react';
import {
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useDataProvider, useNotify, type Identifier } from 'react-admin';

import { DesktopActionCell, DesktopActionHeaderCell } from '../admin/DesktopTableActions';
import {
    BILLING_FEATURES_RESOURCE,
    billingFeatureDataTypeLabels,
    getBillingFeatureLabel,
    type BillingFeature,
    type BillingFeatureDataType,
} from './billingData';
import { formatUpdatedAt } from './billingUtils';

type FeatureDraft = {
    id?: Identifier;
    key: string;
    dataType: BillingFeatureDataType;
    description: string;
};

type BillingFeaturesSectionProps = {
    features: BillingFeature[];
    onChanged: () => Promise<void>;
};

function emptyFeatureDraft(): FeatureDraft {
    return {
        key: '',
        dataType: 2,
        description: '',
    };
}

export function BillingFeaturesSection({ features, onChanged }: BillingFeaturesSectionProps) {
    const notify = useNotify();
    const dataProvider = useDataProvider();

    const [featureDialogOpen, setFeatureDialogOpen] = useState(false);
    const [featureDraft, setFeatureDraft] = useState<FeatureDraft>(emptyFeatureDraft());
    const [pendingDeleteId, setPendingDeleteId] = useState<Identifier | null>(null);
    const [isSavingFeature, setIsSavingFeature] = useState(false);

    const openCreateFeatureDialog = () => {
        setFeatureDraft(emptyFeatureDraft());
        setFeatureDialogOpen(true);
    };

    const openEditFeatureDialog = (feature: BillingFeature) => {
        setFeatureDraft({
            id: feature.id,
            key: feature.key,
            dataType: feature.dataType,
            description: feature.description,
        });
        setFeatureDialogOpen(true);
    };

    const handleDelete = async (feature: BillingFeature) => {
        if (pendingDeleteId !== null) return;

        setPendingDeleteId(feature.id);
        try {
            await dataProvider.delete(BILLING_FEATURES_RESOURCE, { id: feature.id, previousData: feature });
            notify('Feature deleted', { type: 'info' });
            await onChanged();
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Delete failed';
            notify(message, { type: 'error' });
        } finally {
            setPendingDeleteId(null);
        }
    };

    const handleSaveFeature = async () => {
        if (isSavingFeature) return;

        const payload = {
            key: featureDraft.key.trim(),
            dataType: featureDraft.dataType,
            description: featureDraft.description.trim(),
        };

        if (!payload.key) {
            notify('Feature key is required', { type: 'warning' });
            return;
        }

        setIsSavingFeature(true);
        try {
            if (featureDraft.id) {
                await dataProvider.update(BILLING_FEATURES_RESOURCE, {
                    id: featureDraft.id,
                    data: payload,
                    previousData: features.find((feature) => feature.id === featureDraft.id),
                });
                notify('Feature updated', { type: 'info' });
            } else {
                await dataProvider.create(BILLING_FEATURES_RESOURCE, { data: payload });
                notify('Feature created', { type: 'info' });
            }

            setFeatureDialogOpen(false);
            await onChanged();
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unable to save feature';
            notify(message, { type: 'error' });
        } finally {
            setIsSavingFeature(false);
        }
    };

    return (
        <>
            <Stack spacing={2}>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
                    <Typography variant="body2" color="text.secondary">
                        Each feature maps cleanly to the backend feature key, data type, and description.
                    </Typography>
                    <Button variant="contained" onClick={openCreateFeatureDialog} disabled={isSavingFeature || pendingDeleteId !== null}>
                        Add Feature
                    </Button>
                </Stack>
                <Stack spacing={1.5} sx={{ display: { xs: 'flex', lg: 'none' } }}>
                    {features.map((feature) => (
                        <Card key={feature.id} variant="outlined">
                            <CardContent>
                                <Stack spacing={2}>
                                    <Stack spacing={0.5}>
                                        <Typography variant="subtitle1">{getBillingFeatureLabel(feature)}</Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                                            Key: {feature.key}
                                        </Typography>
                                    </Stack>
                                    <Stack spacing={0.75}>
                                        <Chip
                                            label={billingFeatureDataTypeLabels[feature.dataType]}
                                            size="small"
                                            variant="outlined"
                                            sx={{ width: 'fit-content' }}
                                        />
                                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                            {feature.description || 'No description'}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Updated: {formatUpdatedAt(feature.updatedAt)}
                                        </Typography>
                                    </Stack>
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
                                        <Button variant="outlined" size="small" onClick={() => openEditFeatureDialog(feature)} disabled={pendingDeleteId !== null}>
                                            Edit
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            color="error"
                                            onClick={() => void handleDelete(feature)}
                                            disabled={pendingDeleteId !== null}
                                        >
                                            {pendingDeleteId === feature.id ? 'Deleting...' : 'Delete'}
                                        </Button>
                                    </Stack>
                                </Stack>
                            </CardContent>
                        </Card>
                    ))}
                </Stack>
                <TableContainer component={Paper} variant="outlined" sx={{ display: { xs: 'none', lg: 'block' } }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Feature</TableCell>
                                <TableCell>Key</TableCell>
                                <TableCell>Data Type</TableCell>
                                <TableCell>Description</TableCell>
                                <TableCell>Updated</TableCell>
                                <DesktopActionHeaderCell>Edit</DesktopActionHeaderCell>
                                <DesktopActionHeaderCell>Delete</DesktopActionHeaderCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {features.map((feature) => (
                                <TableRow key={feature.id} hover>
                                    <TableCell>{getBillingFeatureLabel(feature)}</TableCell>
                                    <TableCell>{feature.key}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={billingFeatureDataTypeLabels[feature.dataType]}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell>{feature.description}</TableCell>
                                    <TableCell>{formatUpdatedAt(feature.updatedAt)}</TableCell>
                                    <DesktopActionCell>
                                        <IconButton
                                            aria-label="Edit feature"
                                            onClick={() => openEditFeatureDialog(feature)}
                                            disabled={pendingDeleteId !== null}
                                        >
                                            <EditOutlinedIcon fontSize="small" />
                                        </IconButton>
                                    </DesktopActionCell>
                                    <DesktopActionCell>
                                        <IconButton
                                            aria-label="Delete feature"
                                            onClick={() => void handleDelete(feature)}
                                            disabled={pendingDeleteId !== null}
                                        >
                                            <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                    </DesktopActionCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Stack>

            <Dialog open={featureDialogOpen} onClose={() => setFeatureDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>{featureDraft.id ? 'Edit Feature' : 'Add Feature'}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <TextField
                            label="Key"
                            value={featureDraft.key}
                            onChange={(event) =>
                                setFeatureDraft((current) => ({ ...current, key: event.target.value }))
                            }
                            helperText="Example: max_mock_tests"
                            fullWidth
                        />
                        <FormControl fullWidth>
                            <InputLabel id="billing-feature-data-type-label">Data Type</InputLabel>
                            <Select
                                labelId="billing-feature-data-type-label"
                                label="Data Type"
                                value={featureDraft.dataType}
                                onChange={(event) =>
                                    setFeatureDraft((current) => ({
                                        ...current,
                                        dataType: Number(event.target.value) as BillingFeatureDataType,
                                    }))
                                }
                            >
                                {Object.entries(billingFeatureDataTypeLabels).map(([value, label]) => (
                                    <MenuItem key={value} value={Number(value)}>
                                        {label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            label="Description"
                            value={featureDraft.description}
                            onChange={(event) =>
                                setFeatureDraft((current) => ({ ...current, description: event.target.value }))
                            }
                            multiline
                            minRows={3}
                            fullWidth
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setFeatureDialogOpen(false)} disabled={isSavingFeature}>Cancel</Button>
                    <Button variant="contained" onClick={() => void handleSaveFeature()} disabled={isSavingFeature}>
                        {isSavingFeature ? 'Saving...' : 'Save Feature'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
